import {
  ALLOWED_USER_PHOTO_MIME_TYPES,
  MAX_USER_PHOTO_BYTES,
  personName,
  type User,
} from "@clinic/shared";
import { useRef, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Button, Icon, useConfirm, useToast } from "@clinic/ui";
import { useRemoveUserPhoto, useUploadUserPhoto } from "@web/modules/users/queries";
import { errorToast } from "@web/shared/lib/api-error";

export function UserPhotoField({ user }: { readonly user: User }): JSX.Element {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useUploadUserPhoto();
  const remove = useRemoveUserPhoto();
  const { confirm, dialog } = useConfirm("user-photo-confirm-remove");

  const pick = async (file: File | undefined): Promise<void> => {
    if (!file) {
      return;
    }

    if (!ALLOWED_USER_PHOTO_MIME_TYPES.some((allowed) => allowed === file.type)) {
      toast.error("users.photoUnsupported");
      return;
    }

    if (file.size > MAX_USER_PHOTO_BYTES) {
      toast.error("users.photoTooLarge");
      return;
    }

    try {
      await upload.mutateAsync({ id: user.id, file });
      toast.success("users.photoUpdated");
    } catch (error) {
      toast.error(...errorToast(error));
    }
  };

  const clear = (): void =>
    confirm({
      title: "users.confirmRemovePhoto",
      onConfirm: async () => {
        try {
          await remove.mutateAsync(user.id);
          toast.success("users.photoRemoved");
        } catch (error) {
          toast.error(...errorToast(error));
          throw error;
        }
      },
    });

  return (
    <div data-testid="user-photo-field" className="flex items-center gap-3">
      {dialog}
      <Avatar
        data-testid="user-photo-avatar"
        name={personName(user.name, i18n.language)}
        tintKey={user.id}
        src={user.photoUrl}
        size={64}
      />

      <div className="flex flex-col items-start gap-1">
        <p className="text-label text-ink-muted">{t("users.photoHint")}</p>

        <span className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            data-testid="user-photo-input"
            className="hidden"
            accept={ALLOWED_USER_PHOTO_MIME_TYPES.join(",")}
            aria-label={t("users.photo")}
            onChange={(event) => {
              void pick(event.target.files?.[0]);
              event.target.value = "";
            }}
          />

          <Button
            icon={<Icon name="upload" />}
            variant="secondary"
            size="sm"
            data-testid="user-photo-upload"
            isLoading={upload.isPending}
            onClick={() => inputRef.current?.click()}
          >
            {t(user.photoUrl ? "users.replacePhoto" : "users.uploadPhoto")}
          </Button>

          {user.photoUrl && (
            <Button
              icon={<Icon name="trash" />}
              variant="secondary"
              size="sm"
              data-testid="user-photo-remove"
              isLoading={remove.isPending}
              onClick={clear}
            >
              {t("common.delete")}
            </Button>
          )}
        </span>
      </div>
    </div>
  );
}
